import {Injectable} from '@angular/core'
import {HttpClient, HttpParams} from '@angular/common/http'
import {throwError as observableThrowError, Observable} from 'rxjs'
import {tap} from 'rxjs/operators'
import {
  Terminal,
  TerminalType,
  TerminalModel,
  ConfigurationInfo,
  AdvancedConfiguration,
  LifeCycleState,
  Template,
  TerminalAction,
  DataMap,
} from '@app/core/services/rest-api.model'
import {
  ApiSearchRequest,
  ApiSearchResponse,
} from '@app/core/services/api.interface'
import {LoggerService} from '@app/core/services/logger.service'
import {User} from '../user/user.service'

export {
  Terminal,
  TerminalType,
  TerminalModel,
  ConfigurationInfo,
  TerminalConfiguration,
  AdvancedConfiguration,
  TerminalAction,
} from '@app/core/services/rest-api.model'
export interface GetTerminalsRequest extends ApiSearchRequest {
  domain?: string
  contracts: boolean
}
export interface Notes {
  createdAt: any // mutates from int to string
  id: string
  note: string
  updatedAt: number
  editable?: boolean
  public: boolean
  userName?: string
  user: {id: number; domain: string; username: string; emailAddress: string}
}
export interface Note {
  note: string
  user?: User
  createdAt?: Date
  public?: boolean
  id?: string
}

export interface GetTerminalsResponse extends ApiSearchResponse<Terminal> {}

@Injectable()
export class TerminalService {
  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  public getTerminals(
    request: GetTerminalsRequest,
  ): Observable<GetTerminalsResponse> {
    let url = 'api://Terminals'

    if (request.domain) {
      url = 'api://Domains/' + request.domain + '/Terminals'
    }

    let params = new HttpParams()
    if (request.contracts) {
      params = params.set('contracts', '1')
    }
    if (request.search) {
      params = params.set('search', request.search)
    }
    if (request.sort) {
      params = params
        .set('sort', request.sort)
        .set('asc', !request.asc ? 'false' : 'true')
    }
    if (request.offset) {
      params = params.set('offset', request.offset.toString())
    }
    if (request.results) {
      params = params.set('results', request.results.toString())
    }

    return this.http.get<GetTerminalsResponse>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putNotes(id: number, note: Note, noteId: string): Observable<Note> {
    const url =
      'api://Terminals/' + id.toString() + '/Notes/' + noteId.toString()
    return this.http.put<Note>(url, note).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public postNote(id: number, note: Note) {
    const url = 'api://Terminals/' + id.toString() + '/Notes'
    return this.http.post<Note>(url, note).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  public getNotes(id: number) {
    const url = 'api://Terminals/' + id.toString() + '/Notes'
    return this.http.get<[Notes]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  public removeNote(id: number, noteId: number) {
    const url =
      'api://Terminals/' + id.toString() + '/Notes/' + noteId.toString()
    return this.http.delete<Note>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getTerminal(id: number): Observable<Terminal> {
    const url = 'api://Terminals/' + id.toString()
    return this.http.get<Terminal>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public postTerminal(terminal: Terminal): Observable<Terminal> {
    const url = 'api://Terminals'

    return this.http.post<Terminal>(url, terminal).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putTerminal(id: number, terminal: Terminal): Observable<Terminal> {
    const url = `api://Terminals/${id}`

    return this.http.put<Terminal>(url, terminal).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putAttributes(id: number, attributes: DataMap): Observable<DataMap> {
    const url = `api://Terminals/${id}/Attributes`

    return this.http.put<DataMap>(url, attributes).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putAttribute(
    id: number,
    key: string,
    value: string,
  ): Observable<DataMap> {
    const url = `api://Terminals/${id}/Attributes/${key}`

    return this.http.put<DataMap>(url, {key: key, value: value}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public postAttribute(
    id: number,
    key: string,
    value: string,
  ): Observable<DataMap> {
    const url = `api://Terminals/${id}/Attributes`

    return this.http.post<DataMap>(url, {key: key, value: value}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getConfiguration(id: number): Observable<ConfigurationInfo> {
    const url = `api://Terminals/${id}/Configuration`

    const params: HttpParams = new HttpParams().set('advanced', 'true')

    return this.http.get<ConfigurationInfo>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putConfiguration(id: number): Observable<ConfigurationInfo> {
    return observableThrowError(new Error('not implemented'))
  }

  public putConfigurationSettings(
    id: number,
    settings: any,
  ): Observable<ConfigurationInfo> {
    const url = 'api://Terminals/' + id + '/Configuration/Settings'
    return this.http.put<ConfigurationInfo>(url, settings).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public putConfigurationAdvanced(
    id: number,
    advanced: AdvancedConfiguration,
  ): Observable<ConfigurationInfo> {
    const url = 'api://Terminals/' + id + '/Configuration/AdvancedSettings'
    return this.http.put<ConfigurationInfo>(url, advanced).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getAvailableTemplates(id: number): Observable<Template[]> {
    const url = 'api://Terminals/' + id + '/Configuration/AvailableTemplates'
    return this.http.get<Template[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getTemplateConfig(
    domainCode: string,
    templateId: number,
  ): Observable<any> {
    const url =
      'api://Domains/' +
      domainCode +
      '/Templates/' +
      templateId +
      '/Configuration'

    return this.http.get<any>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
  public putTemplate(
    id: number,
    template: Template,
  ): Observable<ConfigurationInfo> {
    const url = 'api://Terminals/' + id + '/Configuration/Template'

    return this.http.put<ConfigurationInfo>(url, template).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getAvailableTerminalTypes(
    domain?: string,
  ): Observable<TerminalType[]> {
    let url
    if (domain === undefined) {
      url = 'api://Terminals/AvailableTerminalTypes'
    } else {
      url = 'api://Domains/' + domain + '/AvailableTerminalTypes'
    }

    return this.http.get<TerminalType[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getTerminalTypeConfiguration(
    code: string,
    domain: string,
  ): Observable<AdvancedConfiguration> {
    const params: HttpParams = new HttpParams().set('advanced', 'true')
    const url = `api://Domains/${domain}/AvailableTerminalTypes/${code}/DefaultConfiguration`
    return this.http.get<AdvancedConfiguration>(url, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getModels(): Observable<TerminalModel[]> {
    const url = 'api://Terminals/Models'
    return this.http.get<TerminalModel[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getLifeCycleStates(): Observable<LifeCycleState[]> {
    const url = 'api://Terminal/LifeCycleStates'

    return this.http.get<LifeCycleState[]>(url).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public getReport(id: number): Observable<any> {
    const url = 'api://Terminals/' + id.toString() + '/Report.xlsx'
    return this.http.get<any>(url)
  }

  public pingShellhub(id: number) {
    const url = 'api://Terminals/' + id.toString() + '/Actions'
    let params = new HttpParams()
    params = params.set('immediate', 'true')
    const action = {
      type: 'launch-shellhub',
    }

    return this.http.post<TerminalAction>(url, action, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }

  public postAction(
    id: number,
    action: TerminalAction,
    immediate?: boolean,
  ): Observable<TerminalAction> {
    const url = 'api://Terminals/' + id.toString() + '/Actions'
    let params = new HttpParams()

    if (immediate) {
      params = params.set('immediate', String(immediate))
    }

    return this.http.post<TerminalAction>(url, action, {params: params}).pipe(
      tap((res) => {
        this.logger.log(res)
      }),
    )
  }
}
